import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bg-white dark:bg-gray-bluer-600 shadow rounded-lg" }
const _hoisted_2 = { class: "px-4 py-5 bg-gray-blue-50 dark:bg-gray-bluer-600 flex justify-between items-center rounded-t-lg" }
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = /*#__PURE__*/_createTextVNode("Media")
const _hoisted_5 = { class: "space-x-3" }
const _hoisted_6 = /*#__PURE__*/_createTextVNode(" New folder ")
const _hoisted_7 = /*#__PURE__*/_createTextVNode(" Upload files ")
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h3", { class: "text-lg leading-6 font-medium text-gray-bluer-400 dark:text-gray-blue-500" }, " New Folder ", -1)
const _hoisted_9 = { class: "py-5 px-7" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("h3", { class: "text-lg leading-6 font-medium text-gray-bluer-400 dark:text-gray-blue-500" }, " Upload files ", -1)
const _hoisted_11 = { class: "p-5" }

import { onMounted, onUnmounted, toRefs, computed, watch, nextTick, ref } from "vue";
import { useStore }                                                  from "vuex";
import { useConfig }                                                 from "./config";
import { userHook }                                                  from "@/hooks/user";
import { MediaModel as Media }                                       from "@greywind/common";

// Components
import {
    AppButton as Button,
    AppTitle as Title,
    AppModal as Modal,
    AppConfirmModal as ConfirmModal
} from "@greywind/ui";

import {
    MediaBreadcrumbs,
    MediaUpload,
    MediaFiles,
    MediaFolderNew
} from './';

import { Presence } from "@ui/components/presence";


export default /*#__PURE__*/_defineComponent({
  props: {
        initialPath:   {
            type:     String,
            required: false
        },
        initialFile:   {
            required: false,
            default:  null
        },
        presence:      {
            type:     Boolean,
            default:  false,
            required: false
        },
        previews:      {
            type:     Boolean,
            default:  true,
            required: false
        },
        canUpload:     {
            type:     Boolean,
            default:  true,
            required: false
        },
        canDeleteFolder: {
            type:     Boolean,
            default:  true,
            required: false
        },
        canDownload: {
            type:     Boolean,
            default:  true,
            required: false
        },
        canReplace: {
            type:     Boolean,
            default:  true,
            required: false
        },
        canBulkUpload: {
            type:     Boolean,
            default:  true,
            required: false
        },
        mode:          {
            type:     String,
            default:  'library',
            required: false
        },
        extensions:    {
            default:  () => ( [] ),
            required: false
        }
    },
  emits: ['selected'],
  setup(__props, { emit }) {

const props = __props



const { initialPath, canBulkUpload, initialFile, presence, canUpload, mode, extensions, previews } = toRefs( props );
const { config }                                                                                   = useConfig();
const store                                                                                        = useStore();
const { activeEnvironment }                                                                        = userHook( store );



const modals      = computed( () => store.state.media.modals );
const path        = computed( () => store.state.media.path );
const files       = computed( () => store.state.media.files );
const loading     = computed( () => store.state.media.loading );

const fileToReplace = ref(null);

onMounted( async (): Promise<void> => {
    await store.dispatch( 'media/resetState' );

    config.value.model = new Media( 'denfield-app-user/denfield-package-media', activeEnvironment.value.meta.api.url );
    await Promise.all(
        [
            store.dispatch( "media/setPreviewsEnabled", previews.value ),
            store.dispatch( "media/setPath", initialPath.value || null ),
            store.dispatch( "media/setMode", mode.value ),
            store.dispatch( "media/setAllowedExtensions", extensions.value || [] ),
            store.dispatch( "media/setFile", initialFile.value || null )
        ]
    );

    window.addEventListener('media:select', handleFileSelect);
} );

onUnmounted(() => {
    window.removeEventListener( 'media:select', handleFileSelect );
});

// Functions
const handleFileSelect = (event): void => {
    emit('selected', event.detail);
}
const replaceFile = ( file: any ): void => {
    fileToReplace.value = file;
    store.dispatch('media/toggleModalOpen', 'upload');
}

const handleUploadFilesClick = () => {
    fileToReplace.value = null;
    store.dispatch('media/toggleModalOpen', 'upload');
}

const load = async (): Promise<void> => {
    await store.dispatch( "media/setLoading", true );
    await store.dispatch( "media/initiate" );

    await nextTick( () => store.dispatch( "media/setLoading", false ) );
}

const closeUploadModal = async (): Promise<void> => {
    await store.dispatch( "media/toggleModalOpen", 'upload' );

    if ( config.value.shouldReload ) {
        await store.dispatch( "media/setLoading", true );
        await store.dispatch( "media/getFiles" );
        await store.dispatch( "media/setLoading", false );

        config.value.shouldReload = false;
    }
}

const folderCreated = async (): Promise<void> => {
    await store.dispatch( "media/setLoading", true );
    await store.dispatch( "media/getFolders" );

    await nextTick( () => store.dispatch( "media/setLoading", false ) );
}

const handleDeleteFolder = async (): Promise<void> => {
    try {
        await store.dispatch( "media/setLoading", true );

        const path = modals.value.deleteFolder.value;

        await config.value.model.deletePath( path );

        await store.dispatch( "media/getFolders" );
    } catch ( { response } ) {
        if ( !response ) return;

        await store.dispatch(
            "messages/push",
            {
                title:     'Path remove failed',
                text:      response.data.message,
                theme:     'error',
                icon:      'fontawesome.circle-exclamation',
                closeable: true,
                autoClose: 10000
            }
        );
    } finally {
        await store.dispatch( "media/toggleModalOpen", 'deleteFolder' );
        await store.dispatch( "media/setLoading", false );
    }
}

const handleDeleteFile = async (): Promise<void> => {
    await store.dispatch( "media/setLoading", true );

    const file         = modals.value.deleteFile.value;
    const updatedFiles = [ ...files.value ];

    // Remove file from filesystem.
    await config.value.model.delete( file.id );

    // Update list of the files.
    const fileIndex = files.value.findIndex( ( { id } ) => id === file.id );
    updatedFiles.splice( fileIndex, 1 );

    await store.dispatch( "media/setFiles", updatedFiles )
    await store.dispatch( "media/toggleModalOpen", 'deleteFile' );
    await store.dispatch( "media/setLoading", false );
}

watch( () => path.value, () => load(), { deep: true, immediate: true } );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(Title), {
          icon: "fontawesome.images",
          "icon-background": true
        }, {
          default: _withCtx(() => [
            _hoisted_4
          ]),
          _: 1
        }),
        _createVNode(_unref(MediaBreadcrumbs))
      ]),
      _createElementVNode("div", _hoisted_5, [
        (_unref(presence))
          ? (_openBlock(), _createBlock(_unref(Presence), {
              key: 0,
              channel: `presence.${_unref(activeEnvironment).id}.media`
            }, null, 8, ["channel"]))
          : _createCommentVNode("", true),
        (_unref(canUpload))
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 1,
              id: "new-folder-button",
              size: "xsmall",
              icon: "fontawesome.folder-plus",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(store).dispatch('media/toggleModalOpen', 'folder'))),
              pill: false
            }, {
              default: _withCtx(() => [
                _hoisted_6
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(canUpload))
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 2,
              id: "new-button",
              size: "xsmall",
              icon: "fontawesome.arrow-up-from-line",
              onClick: handleUploadFilesClick,
              pill: false
            }, {
              default: _withCtx(() => [
                _hoisted_7
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(_unref(MediaFiles), {
      ref: "mediaFiles",
      "can-delete-folder": __props.canDeleteFolder,
      "can-replace": __props.canReplace,
      "can-download": __props.canDownload,
      onReplace: replaceFile
    }, null, 8, ["can-delete-folder", "can-replace", "can-download"]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_unref(ConfirmModal), {
        open: _unref(modals).deleteFolder.open,
        onClose: _cache[1] || (_cache[1] = ($event: any) => (_unref(store).dispatch('media/toggleModalOpen', 'deleteFolder'))),
        title: "Are you sure you want to delete this folder?",
        copy: "Once the folder has been deleted you wont be able to get it back.",
        "action-text": "Continue",
        onConfirm: handleDeleteFolder
      }, null, 8, ["open"]),
      _createVNode(_unref(ConfirmModal), {
        style: {"z-index":"9999999 !important"},
        open: _unref(modals).deleteFile.open,
        onClose: _cache[2] || (_cache[2] = ($event: any) => (_unref(store).dispatch('media/toggleModalOpen', 'deleteFile'))),
        title: "Are you sure you want to delete this file?",
        copy: "Once the file has been deleted you wont be able to get it back.",
        "action-text": "Continue",
        onConfirm: handleDeleteFile
      }, null, 8, ["open"])
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (_unref(canUpload))
        ? (_openBlock(), _createBlock(_unref(Modal), {
            key: 0,
            open: _unref(modals).folder.open,
            onClose: _cache[4] || (_cache[4] = ($event: any) => (_unref(store).dispatch('media/toggleModalOpen', 'folder'))),
            "width-classes": "sm:max-w-xl sm:w-full",
            "inner-border": true,
            style: {"z-index":"9999999999!important"}
          }, {
            title: _withCtx(() => [
              _hoisted_8
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_9, [
                _createVNode(_unref(MediaFolderNew), {
                  model: _unref(config).model,
                  onClose: _cache[3] || (_cache[3] = ($event: any) => (_unref(store).dispatch('media/toggleModalOpen', 'folder'))),
                  onCreated: folderCreated
                }, null, 8, ["model"])
              ])
            ]),
            _: 1
          }, 8, ["open"]))
        : _createCommentVNode("", true)
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (_unref(canUpload))
        ? (_openBlock(), _createBlock(_unref(Modal), {
            key: 0,
            open: _unref(modals).upload.open,
            onClose: closeUploadModal,
            "width-classes": "sm:max-w-4xl sm:w-full",
            "inner-border": true,
            style: {"z-index":"9999999999!important"}
          }, {
            title: _withCtx(() => [
              _hoisted_10
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_unref(MediaUpload), {
                  file: fileToReplace.value,
                  "can-bulk-upload": _unref(canBulkUpload),
                  model: _unref(config).model,
                  onClose: closeUploadModal,
                  onReload: _cache[5] || (_cache[5] = ($event: any) => (_unref(config).shouldReload = true))
                }, null, 8, ["file", "can-bulk-upload", "model"])
              ])
            ]),
            _: 1
          }, 8, ["open"]))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})